import { Injectable, NgZone, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import * as signalR from '@microsoft/signalr';

import { BcEmployeeAuthFacade } from '@bayclubs/employee-auth';

import { environment } from '@environments/environment';
import { LoggingService } from './logging.service';

@Injectable({
    providedIn: 'root'
})
export class SignalRService implements OnDestroy {
    public connected$ = new BehaviorSubject<boolean>(false);

    private hubConnection: signalR.HubConnection;
    private reconnectCounter = 0;
    private reconnectTimeouts = [0, 3000, 5000, 15000, 30000, 60000];
    private connectingStarted = false;

    private isAuthenticatedSubscription: Subscription;

    constructor(private auth: BcEmployeeAuthFacade, private log: LoggingService, private zone: NgZone) {
        this.zone.runOutsideAngular(() => {
            this.buildConnection();
            this.registerCallbacks();
        });

        this.isAuthenticatedSubscription = this.auth.isAuthenticated$.subscribe(isAuthenticated => {
            if (isAuthenticated) {
                this.startConnection();
            }
        });

        if (this.auth.getIsAuthenticatedSnapshot()) {
            this.startConnection();
        }
    }

    public invokeMethod(methodName: string, argument: any) {
        if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
            this.zone.runOutsideAngular(() => {
                this.hubConnection
                    .invoke(methodName, argument)
                    .then(() => {
                        this.zone.run(() => this.log.logApplicationInfo('Successfully invoked ' + methodName + ' with arg: ' + argument));
                    })
                    .catch(err => {
                        this.zone.run(() => this.log.logApplicationError(err, 'Error when invoking ' + methodName));
                    });
            });
        } else {
            this.log.logWarn('Unable to invoke method ' + methodName + '. Connection is closed.');
        }
    }

    public registerListener(methodName: string, newMethod: (...args: any[]) => void) {
        this.zone.runOutsideAngular(() => {
            this.hubConnection.on(methodName, (...args) => {
                this.zone.run(() => newMethod(...args));
            });
        });
    }

    public startConnection() {
        if (!this.connectingStarted && this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
            this.log.logApplicationInfo(`SignalR connection starting`);
            this.connectingStarted = true;

            this.zone.runOutsideAngular(() => {
                this.hubConnection
                    .start()
                    .then(() => {
                        this.zone.run(() => {
                            this.connectingStarted = false;
                            this.reconnectCounter = 0;
                            this.log.logApplicationInfo('SignalR connection started');
                            this.connected$.next(true);
                        });
                    })
                    .catch(err => {
                        this.zone.run(() => {
                            this.connectingStarted = false;
                            this.log.logWarn('Error while starting SignalR connection', null, err);
                            throw err;
                        });
                    });
            });
        }
    }

    private buildConnection() {
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withAutomaticReconnect(this.reconnectTimeouts)
            .withUrl(environment.signalRHubUrl, {
                accessTokenFactory: () => (this.auth.getHasTokenSnapshot() ? this.auth.getTokenSnapshot().access_token : null),
                logger: {
                    log: (logLevel, message) => {
                        switch (logLevel) {
                            case signalR.LogLevel.Information:
                                this.log.logApplicationInfo(message);
                                break;
                            case signalR.LogLevel.Warning:
                                this.log.logWarn(message);
                                break;
                            case signalR.LogLevel.Error:
                                // signalR is not crucial, so errors are only warnings for us
                                this.log.logWarn(message);
                                break;
                            case signalR.LogLevel.Critical:
                                this.log.logWarn(message);
                                break;
                        }
                    }
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();
    }

    private registerCallbacks() {
        this.hubConnection.onclose(error => {
            this.zone.run(() => {
                this.connected$.next(false);
                this.log.logApplicationError(error, 'Disconnected from SignalR.');
            });
        });

        this.hubConnection.onreconnecting(error => {
            this.zone.run(() => {
                this.reconnectCounter++;
                this.log.logWarn(`Connection lost to SignalR due to ${error}. Trying to reconnect ...`);
            });
        });

        this.hubConnection.onreconnected(error => {
            this.zone.run(() => {
                this.log.logWarn(`Connection to SignalR reestablished during ${this.reconnectCounter} attempt.`);
                this.reconnectCounter = 0;
            });
        });
    }

    ngOnDestroy(): void {
        this.log.logApplicationInfo('SignalR connection stopping');
        this.hubConnection
            .stop()
            .then(() => {
                this.log.logApplicationInfo('SignalR connection stopped');
            })
            .catch(err => {
                this.log.logWarn(err, 'Error while stopping SignalR connection', err);
            });

        this.isAuthenticatedSubscription?.unsubscribe();
    }
}
